import {
    bexHttpClient
} from '../../clients';
import {
    CreateInstructionRequest,
    UpdateInstructionRequest,
    DeleteInstructionRequest,
    CreateInstructionResponse,
    UpdateInstructionResponse,
    GetInstructionResponse,
    ListAllInstructionsResponse,
    GetLinkedOpcodesResponse,
    DeleteInstructionResponse,
    InstructionModelType
} from '../bexService.types';

/**
 * Service class for performing instruction operations.
 */
class InstructionService {

    /**
     * Creates a new model (Instruction).
     * @param {CreateInstructionRequest} data - The data for the new model.
     * @returns {Promise<CreateInstructionResponse>} The response containing the created model.
     */
    async createModel(data: CreateInstructionRequest): Promise<CreateInstructionResponse> {
        const response = await bexHttpClient.post('/redis/models/model/create', data);
        return response;
    }

    /**
     * Updates an existing model (Instruction).
     * @param {UpdateInstructionRequest} data - The updated data for the model.
     * @returns {Promise<UpdateInstructionResponse>} The response containing the updated model.
     */
    async updateModel(data: UpdateInstructionRequest): Promise<UpdateInstructionResponse> {
        const response = await bexHttpClient.put('/redis/models/model/update', data);
        return response;
    }

    /**
     * Gets a model (Instruction) by ID.
     * @param {string} organizationId - The ID of the organization.
     * @param {string} subOrganizationId - The ID of the sub-organization.
     * @param {InstructionModelType} modelType - The type of the model.
     * @param {string} unique_name - The unique name of the model.
     * @returns {Promise<GetInstructionResponse>} The response containing the model.
     */
    async getModel(organizationId: string, subOrganizationId: string, modelType: InstructionModelType, unique_name: string): Promise<GetInstructionResponse> {
        const response = await bexHttpClient.get(
            `/redis/models/model/get?organizationId=${organizationId}&subOrganizationId=${subOrganizationId}&modelType=${modelType}&unique_name=${unique_name}`
        );
        return response;
    }

    /**
     * Gets a model opcode link list
     * @param {string} organizationId - The ID of the organization.
     * @param {string} subOrganizationId - The ID of the sub-organization.
     * @param {InstructionModelType} modelType - The type of the model.
     * @param {string} unique_name - The unique name of the model.
     * @returns {Promise<GetLinkedOpcodesResponse>} The response containing the model.
     */
    async getLinkedOpcodes(organizationId: string, subOrganizationId: string, modelType: InstructionModelType, unique_name: string): Promise<GetLinkedOpcodesResponse> {
        const response = await bexHttpClient.get(
            `/redis/models/model/get-linked-opcodes?organizationId=${organizationId}&subOrganizationId=${subOrganizationId}&modelType=${modelType}&unique_name=${unique_name}`
        );
        return response;
    }

    /**
     * Lists all models (Instructions).
     * @param {string} organizationId - The ID of the organization.
     * @param {string} subOrganizationId - The ID of the sub-organization.
     * @param {InstructionModelType | null} modelType - The type of the models to list (optional).
     * @returns {Promise<ListAllInstructionsResponse>} The response containing the list of models.
     */
    async listModels(organizationId: string, subOrganizationId: string, modelType: InstructionModelType | null = null, page?: number, limit?: number): Promise<ListAllInstructionsResponse> {
        let url = `/redis/models/model/list?organizationId=${organizationId}&subOrganizationId=${subOrganizationId}`;
        if (modelType) url += `&modelType=${modelType}`;
        if (page !== undefined) url += `&page=${page}`;
        if (limit !== undefined) url += `&limit=${limit}`;
        const response = await bexHttpClient.get(url);
        return response;
    }

    /**
     * Deletes models by unique names.
     * @param {DeleteInstructionRequest} data - The payload containing the list of unique names to delete.
     * @returns {Promise<DeleteInstructionResponse>} The response confirming the deletion.
     */
    async deleteModels(data: DeleteInstructionRequest): Promise<DeleteInstructionResponse> {
        const response = await bexHttpClient.delete('/redis/models/model/delete', {
            data
        });
        return response;
    }
}

const instructionService = new InstructionService();
export { instructionService };